import React, { useState, useMemo } from 'react';
import { Input, Button, Spin, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ParentCustomFieldsVirtualList from './ParentCustomFieldsVirtualList';
import { CustomFieldGroup, BrandCustomField } from '../../../../../types/custom_fields';
import saveTranslation from '../../../../constants/SaveTranslation.json';
import ParentCustomFieldEditDrawer from './ParentCustomFieldEditDrawer';
import ParentCustomFieldsGroupDrawer from './ParentCustomFieldsGroupDrawer';
import { ApplicationState } from '../../../../reducers';
import { hasPermission } from '../../../../utils/Permissions';
import { Brand } from '../../../../../types/brand';

interface ParentCustomFieldsPageProps {
  groups: CustomFieldGroup[];
  brands: Brand[];
  loading: boolean;
  saving: boolean;
  hasPendingChanges: boolean;
  hasPendingOrderChanges?: boolean;
  onBrandChange: (customFieldId: number, brandIds: number[]) => void;
  onGroupOrderChange?: (groups: CustomFieldGroup[]) => void;
  onSaveChanges: () => void;
  onCancelChanges?: () => void;
  onAddCustomField: () => void;
  onEditCustomField: (customFieldId: number) => void;
  showEditDrawer: boolean;
  selectedCustomFieldId: number | null;
  onCloseEditDrawer: () => void;
  onFieldSaved: () => void;
  onDeleteCustomField: (customFieldId: number) => void;
  deletingCustomField: boolean;
  onAddGroup: () => void;
  onEditGroup: (groupId: number | string) => void;
  showGroupDrawer: boolean;
  selectedGroupId: number | string | null;
  onCloseGroupDrawer: () => void;
  onAddGroupAction: (name: string) => void;
  onUpdateGroupAction: (id: number, name: string) => void;
  onDeleteGroupAction: (id: number | string) => void;
  onApplyAllToAllBrands: () => void;
}

const ParentCustomFieldsPage: React.FC<ParentCustomFieldsPageProps> = ({
  groups,
  brands,
  loading,
  saving,
  hasPendingChanges,
  hasPendingOrderChanges,
  onBrandChange,
  onGroupOrderChange,
  onSaveChanges,
  onCancelChanges,
  onAddCustomField,
  onEditCustomField,
  showEditDrawer,
  selectedCustomFieldId,
  onCloseEditDrawer,
  onFieldSaved,
  onDeleteCustomField,
  deletingCustomField,
  onAddGroup,
  onEditGroup,
  showGroupDrawer,
  selectedGroupId,
  onCloseGroupDrawer,
  onAddGroupAction,
  onUpdateGroupAction,
  onDeleteGroupAction,
  onApplyAllToAllBrands,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const user = useSelector((state: ApplicationState) => state.user.user);
  const canEditCustomFields: boolean = hasPermission(user, 'can_manage_custom_fields');

  const selectedCustomField = useMemo(() => {
    if (!selectedCustomFieldId) {
      return undefined;
    }
    return groups
      .flatMap(g => g.fields || [])
      .find((field: BrandCustomField) => field.id === selectedCustomFieldId);
  }, [selectedCustomFieldId, groups]);

  const handleApplyAllToAllBrands = () => {
    onApplyAllToAllBrands();
  };

  const handleAddNewGroup = () => {
    onAddGroup();
  };

  const handleAddNewCustomField = () => {
    onAddCustomField();
  };

  if (loading) {
    return <Spin className="spinner-center" style={{ marginTop: '20px' }} />;
  }

  return (
    <div className="page-layout">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <Input.Search
            className="page-layout__top-bar__search parent-custom-fields-search"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder={t('settings:customFields.searchCustomFields')}
            allowClear
          />
          <div className="page-layout__top-bar__actions">
            <Space size="middle">
              <Button onClick={handleApplyAllToAllBrands}>
                {t('settings:customFields.applyAllToAllBrands')}
              </Button>
              <Button onClick={handleAddNewGroup}>{t('settings:customFields.addNewGroup')}</Button>
              <Button type="primary" onClick={handleAddNewCustomField}>
                {t('settings:customFields.addNewCustomField')}
              </Button>
            </Space>
          </div>
        </div>
      </div>
      <div className="page-layout__content">
        <div className="parent-custom-fields-content parent-custom-fields-page__content-container">
          <ParentCustomFieldsVirtualList
            groups={groups}
            keywords={inputValue}
            brands={brands}
            onBrandChange={onBrandChange}
            onGroupOrderChange={onGroupOrderChange}
            hasPendingOrderChanges={hasPendingOrderChanges}
            onEdit={(id: number, type: string) => {
              if (type === 'field') {
                onEditCustomField(id);
              } else if (type === 'group') {
                onEditGroup(id);
              }
            }}
          />
        </div>
      </div>
      {hasPendingChanges && (
        <React.Fragment>
          <div className="status-bar flex items-center">
            {/* Status messages can be added here if needed */}
          </div>
          <div className="page__bottom-save flex items-center">
            <div className="flex items-center flex-1">
              <div className="flex-1 flex justify-end">
                <Button className="page__bottom-save-cancel" onClick={onCancelChanges}>
                  {saveTranslation.cancel}
                </Button>
                <Button type="primary" onClick={onSaveChanges} loading={saving}>
                  {saveTranslation.save}
                </Button>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      <ParentCustomFieldEditDrawer
        visible={showEditDrawer}
        canEditCustomFields={canEditCustomFields}
        closeDrawer={onCloseEditDrawer}
        onFieldSaved={onFieldSaved}
        customFieldsLength={groups.flatMap(g => g.fields || []).length}
        customField={selectedCustomField}
        customFields={groups.flatMap(g => g.fields || [])}
        groups={groups}
        onDelete={onDeleteCustomField}
        isDeleting={deletingCustomField}
      />

      <ParentCustomFieldsGroupDrawer
        visible={showGroupDrawer}
        canEditGroups={canEditCustomFields}
        groupNames={groups.map(g => g.name)}
        selectedGroup={groups.find(g => g.id === selectedGroupId)}
        closeDrawer={onCloseGroupDrawer}
        addGroup={onAddGroupAction}
        changeGroupName={onUpdateGroupAction}
        deleteGroup={onDeleteGroupAction}
      />
    </div>
  );
};

export default ParentCustomFieldsPage;
